import React, { useCallback, useEffect, useState } from "react";
import { publicOrigin } from "@/lib/landerDomain";
import { toast } from "sonner";
import { Copy, PaperPlaneTilt } from "@phosphor-icons/react";
import api from "@/lib/api";
import { BTN_BLACK, BTN_WHITE, errText } from "@/components/pageSettings/AdminModal";

const when = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

function LastReply({ r }) {
  if (!r) return <span className="text-zinc-500">No reply yet</span>;
  const ok = r.decision === "approved";
  return (
    <span className={ok ? "text-emerald-800" : "text-amber-900"}>
      {ok ? "Approved" : "Asked for changes"}{r.name ? ` by ${r.name}` : ""} on {when(r.at)}
      {r.note && <span className="block text-zinc-700">&ldquo;{r.note}&rdquo;</span>}
    </span>
  );
}

/** Private view-only link the firm opens to approve its thank-you page or ask for changes. */
export function ShareReviewLink({ b }) {
  const [links, setLinks] = useState([]);
  const [fresh, setFresh] = useState("");
  const [busy, setBusy] = useState(false);
  const p = `ty-share-${b.code}`;
  const load = useCallback(() => {
    api.get(`/admin/buyers/${b.id}/ty-review-links`).then((r) => setLinks(r.data.links)).catch(() => {});
  }, [b.id]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/buyers/${b.id}/ty-review-links`);
      const url = `${publicOrigin()}${data.path}`;
      setFresh(url);
      navigator.clipboard?.writeText(url).then(() => toast.success("Link copied. Send it to the firm"), () => {});
      load();
    } catch (e) {
      toast.error(errText(e, "Couldn't make a link"));
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (id) => {
    try { await api.post(`/admin/ty-review-links/${id}/revoke`); toast.success("Link turned off"); load(); } catch (e) { toast.error(errText(e, "That didn't work")); }
  };
  const live = links.filter((l) => !l.revoked_at && new Date(l.expires_at) > new Date());
  return (
    <div className="mt-3 border-2 border-black p-3" data-testid={p}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="label-mono">Share with the firm</div>
        <button type="button" className={`${BTN_BLACK} ml-auto`} onClick={create} disabled={busy} data-testid={`${p}-create`}>
          <PaperPlaneTilt size={11} weight="bold" /> {busy ? "Making link…" : "Make a private link"}
        </button>
      </div>
      <p className="mt-1 text-[11px] text-zinc-600">The firm can look at the page and approve it or ask for changes. They can&apos;t edit anything. Links last 14 days; we email you when they reply.</p>
      {fresh && (
        <div className="mt-2 flex items-center gap-2 bg-zinc-100 px-2 py-1">
          <span className="truncate font-mono text-[11px]" data-testid={`${p}-url`}>{fresh}</span>
          <button type="button" className={BTN_WHITE} onClick={() => navigator.clipboard?.writeText(fresh).then(() => toast.success("Copied"))} data-testid={`${p}-copy`}>
            <Copy size={11} weight="bold" /> Copy
          </button>
        </div>
      )}
      {live.map((l) => (
        <div key={l.id} className="mt-2 flex items-start gap-2 border-t border-zinc-200 pt-2 text-[11px]" data-testid={`${p}-link-${l.id}`}>
          <div className="flex-1">
            <div className="text-zinc-500">Sent {when(l.created_at)} by {l.created_by_name} · ends {when(l.expires_at)}</div>
            <LastReply r={(l.responses || []).slice(-1)[0]} />
          </div>
          <button type="button" className={BTN_WHITE} onClick={() => revoke(l.id)} data-testid={`${p}-revoke-${l.id}`}>Turn off</button>
        </div>
      ))}
    </div>
  );
}
